(() => {
  'use strict';

  if (window.__aureliaWhatsAppSafeAreaReady) return;
  window.__aureliaWhatsAppSafeAreaReady = true;

  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const candidateSelector = [
    'iframe[src*="bitespeed" i]',
    'iframe[src*="whatsapp" i]',
    'iframe[title*="whatsapp" i]',
    '[id*="bitespeed-whatsapp" i]',
    '[class*="bitespeed-whatsapp" i]',
    '[aria-label*="whatsapp" i]',
    '[title*="whatsapp" i]',
    'a[href*="wa.me"]',
    'a[href*="api.whatsapp.com"]',
    'a[href*="whatsapp.com"]'
  ].join(',');

  const marker = /bitespeed|whatsapp/i;
  const touchedHosts = new Set();
  const originalStyles = new WeakMap();
  const properties = [
    'position',
    'inset-block-start',
    'inset-block-end',
    'inset-inline-start',
    'inset-inline-end',
    'top',
    'right',
    'bottom',
    'left',
    'margin',
    'z-index'
  ];

  const nextParent = (element) => {
    if (element.parentElement) return element.parentElement;
    const root = element.getRootNode?.();
    return root instanceof ShadowRoot ? root.host : null;
  };

  const isSmallVisibleElement = (element) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.width <= 280 &&
      rect.height <= 280
    );
  };

  const descriptorFor = (element) => {
    const className = typeof element.className === 'string' ? element.className : '';
    return [
      element.tagName || '',
      element.id || '',
      className,
      element.getAttribute?.('src') || '',
      element.getAttribute?.('href') || '',
      element.getAttribute?.('title') || '',
      element.getAttribute?.('aria-label') || ''
    ].join(' ');
  };

  const findMovableHost = (candidate) => {
    let current = candidate;

    for (let depth = 0; current && current !== document.documentElement && depth < 10; depth += 1) {
      const descriptor = descriptorFor(current);
      const position = window.getComputedStyle(current).position;

      if (position === 'fixed' && marker.test(descriptor) && isSmallVisibleElement(current)) {
        return current;
      }

      if (position === 'fixed' && isSmallVisibleElement(current) && marker.test(descriptorFor(candidate))) {
        return current;
      }

      current = nextParent(current);
    }

    if (
      marker.test(descriptorFor(candidate)) &&
      isSmallVisibleElement(candidate) &&
      window.getComputedStyle(candidate).position === 'fixed'
    ) {
      return candidate;
    }

    return null;
  };

  const captureOriginalStyles = (host) => {
    if (originalStyles.has(host)) return;

    const snapshot = {};
    properties.forEach((property) => {
      snapshot[property] = {
        value: host.style.getPropertyValue(property),
        priority: host.style.getPropertyPriority(property)
      };
    });
    originalStyles.set(host, snapshot);
  };

  const widgetBottom = () => (
    document.body.classList.contains('template--product')
      ? 'calc(env(safe-area-inset-bottom, 0px) + 10.4rem)'
      : 'calc(env(safe-area-inset-bottom, 0px) + 7.2rem)'
  );

  const liftWidget = (candidate) => {
    if (!mobileQuery.matches || !(candidate instanceof Element)) return;

    const host = findMovableHost(candidate);
    if (!host) return;

    captureOriginalStyles(host);
    touchedHosts.add(host);

    const bottom = widgetBottom();
    const left = 'max(1.4rem, env(safe-area-inset-left, 0px))';

    host.style.setProperty('position', 'fixed', 'important');
    host.style.setProperty('inset-block-start', 'auto', 'important');
    host.style.setProperty('inset-block-end', bottom, 'important');
    host.style.setProperty('inset-inline-start', left, 'important');
    host.style.setProperty('inset-inline-end', 'auto', 'important');
    host.style.setProperty('top', 'auto', 'important');
    host.style.setProperty('right', 'auto', 'important');
    host.style.setProperty('bottom', bottom, 'important');
    host.style.setProperty('left', left, 'important');
    host.style.setProperty('margin', '0', 'important');
    host.style.setProperty('z-index', '2147483000', 'important');
    host.dataset.aureliaWhatsappLifted = 'true';
  };

  const restoreWidgets = () => {
    touchedHosts.forEach((host) => {
      const snapshot = originalStyles.get(host);
      if (!snapshot) return;

      properties.forEach((property) => {
        const original = snapshot[property];
        if (original.value) {
          host.style.setProperty(property, original.value, original.priority);
        } else {
          host.style.removeProperty(property);
        }
      });

      delete host.dataset.aureliaWhatsappLifted;
    });

    touchedHosts.clear();
  };

  const scanRoot = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    if (root instanceof Element && root.matches(candidateSelector)) {
      liftWidget(root);
    }

    root.querySelectorAll(candidateSelector).forEach((candidate) => {
      liftWidget(candidate);
      if (candidate.shadowRoot) scanRoot(candidate.shadowRoot);
    });
  };

  const scan = () => {
    if (!mobileQuery.matches) {
      restoreWidgets();
      return;
    }
    scanRoot(document);
  };

  const queueScans = () => {
    [0, 250, 800, 1800, 4000, 8000].forEach((delay) => {
      window.setTimeout(scan, delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueScans, { once: true });
  } else {
    queueScans();
  }

  window.addEventListener('load', queueScans, { once: true });
  window.addEventListener('pageshow', queueScans);
  window.addEventListener('resize', scan, { passive: true });
  window.addEventListener('orientationchange', () => window.setTimeout(scan, 250), { passive: true });
  document.addEventListener('shopify:section:load', queueScans);

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', scan);
  } else {
    mobileQuery.addListener(scan);
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) scanRoot(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
